import React, { useState, useMemo } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import StakeholderTable from './components/StakeholderTable';
import StakeholderFilters from './components/StakeholderFilters';
import StakeholderStats from './components/StakeholderStats';
import RoleDistribution from './components/RoleDistribution';
import PendingActions from './components/PendingActions';
import StakeholderMobileCard from './components/StakeholderMobileCard';
import InviteStakeholderModal from './components/InviteStakeholderModal';

const StakeholderManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  // Mock stakeholder data
  const stakeholders = [
    {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah.johnson@luxuryrealty.com",
      phone: "+1 (555) 123-4567",
      role: "Property Owner",
      company: "Luxury Realty Group",
      status: "Active",
      transactionCount: 3,
      lastActivity: "2 hours ago",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150",
      isOnline: true,
      completionRate: 95,
      avgResponseTime: "2.3 hrs"
    },
    {
      id: 2,
      name: "Michael Chen",
      email: "m.chen@legalpartners.com",
      phone: "+1 (555) 234-5678",
      role: "Lawyer",
      company: "Legal Partners LLC",
      status: "Active",
      transactionCount: 8,
      lastActivity: "1 hour ago",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150",
      isOnline: true,
      completionRate: 98,
      avgResponseTime: "1.8 hrs"
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      email: "emily.r@structuralexperts.com",
      phone: "+1 (555) 345-6789",
      role: "Engineer",
      company: "Structural Experts Inc",
      status: "Active",
      transactionCount: 5,
      lastActivity: "30 minutes ago",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150",
      isOnline: true,
      completionRate: 92,
      avgResponseTime: "3.1 hrs"
    },
    {
      id: 4,
      name: "David Thompson",
      email: "d.thompson@notaryservices.com",
      phone: "+1 (555) 456-7890",
      role: "Notary",
      company: "Professional Notary Services",
      status: "Pending",
      transactionCount: 2,
      lastActivity: "1 day ago",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150",
      isOnline: false,
      completionRate: 88,
      avgResponseTime: "4.2 hrs"
    },
    {
      id: 5,
      name: "Lisa Wang",
      email: "lisa.wang@premiumbrokers.com",
      phone: "+1 (555) 567-8901",
      role: "Broker",
      company: "Premium Brokers",
      status: "Active",
      transactionCount: 12,
      lastActivity: "15 minutes ago",
      avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150",
      isOnline: true,
      completionRate: 96,
      avgResponseTime: "1.5 hrs"
    },
    {
      id: 6,
      name: "Robert Martinez",
      email: "r.martinez@citynotary.com",
      phone: "+1 (555) 678-9012",
      role: "Notary",
      company: "City Notary Public",
      status: "Inactive",
      transactionCount: 1,
      lastActivity: "1 week ago",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150",
      isOnline: false,
      completionRate: 75,
      avgResponseTime: "6.8 hrs"
    },
    {
      id: 7,
      name: "Jennifer Adams",
      email: "j.adams@commerciallaw.com",
      phone: "+1 (555) 789-0123",
      role: "Lawyer",
      company: "Commercial Law Associates",
      status: "Active",
      transactionCount: 6,
      lastActivity: "3 hours ago",
      avatar: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150",
      isOnline: false,
      completionRate: 94,
      avgResponseTime: "2.7 hrs"
    },
    {
      id: 8,
      name: "James Wilson",
      email: "james.wilson@civilengineering.com",
      phone: "+1 (555) 890-1234",
      role: "Engineer",
      company: "Civil Engineering Solutions",
      status: "Active",
      transactionCount: 4,
      lastActivity: "45 minutes ago",
      avatar: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=150",
      isOnline: true,
      completionRate: 90,
      avgResponseTime: "3.5 hrs"
    }
  ];

  // Mock stats data
  const stats = {
    total: stakeholders?.length,
    activeThisWeek: stakeholders?.filter(s => s?.status === 'Active')?.length,
    pendingInvitations: stakeholders?.filter(s => s?.status === 'Pending')?.length,
    avgResponseTime: "2.8 hrs"
  };

  // Mock role distribution data
  const roleData = [
    { name: 'Property Owner', value: stakeholders?.filter(s => s?.role === 'Property Owner')?.length },
    { name: 'Lawyer', value: stakeholders?.filter(s => s?.role === 'Lawyer')?.length },
    { name: 'Engineer', value: stakeholders?.filter(s => s?.role === 'Engineer')?.length },
    { name: 'Notary', value: stakeholders?.filter(s => s?.role === 'Notary')?.length },
    { name: 'Broker', value: stakeholders?.filter(s => s?.role === 'Broker')?.length }
  ]?.filter(item => item?.value > 0);

  // Mock pending actions data
  const pendingActions = [
    {
      id: 1,
      type: 'signature_required',
      title: 'Digital Signature Required',
      description: 'Property deed requires signature from David Thompson',
      stakeholder: 'David Thompson',
      priority: 'High',
      dueDate: 'Today'
    },
    {
      id: 2,
      type: 'document_review',
      title: 'Document Review Pending',
      description: 'Engineering report needs review and approval',
      stakeholder: 'Emily Rodriguez',
      priority: 'Medium',
      dueDate: 'Tomorrow'
    },
    {
      id: 3,
      type: 'permission_update',
      title: 'Permission Update Required',
      description: 'Update access permissions for new transaction',
      stakeholder: 'Michael Chen',
      priority: 'Low',
      dueDate: 'This Week'
    },
    {
      id: 4,
      type: 'invitation_pending',
      title: 'Invitation Response Pending',
      description: 'Waiting for response to stakeholder invitation',
      stakeholder: 'Robert Martinez',
      priority: 'Medium',
      dueDate: '2 days ago'
    }
  ];

  // Filter stakeholders based on search and filters
  const filteredStakeholders = useMemo(() => {
    return stakeholders?.filter(stakeholder => {
      const matchesSearch = !searchQuery || 
        stakeholder?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        stakeholder?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        stakeholder?.company?.toLowerCase()?.includes(searchQuery?.toLowerCase());
      
      const matchesRole = roleFilter === 'all' || stakeholder?.role === roleFilter;
      const matchesStatus = statusFilter === 'all' || stakeholder?.status === statusFilter;
      const matchesTransaction = transactionFilter === 'all'; // Simplified for demo
      
      return matchesSearch && matchesRole && matchesStatus && matchesTransaction;
    });
  }, [stakeholders, searchQuery, roleFilter, statusFilter, transactionFilter]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setRoleFilter('all');
    setStatusFilter('all');
    setTransactionFilter('all');
  };

  const handleViewProfile = (stakeholder) => {
    console.log('View profile:', stakeholder);
    // Navigate to stakeholder profile or open modal
  };

  const handleEditPermissions = (stakeholder) => {
    console.log('Edit permissions:', stakeholder);
    // Open permissions modal
  };

  const handleSendMessage = (stakeholder) => {
    console.log('Send message:', stakeholder);
    // Open messaging interface
  };

  const handleAssignTransaction = (stakeholder) => {
    console.log('Assign transaction:', stakeholder);
    // Open transaction assignment modal
  };

  const handleActionClick = (action) => {
    console.log('Handle action:', action);
    // Navigate to appropriate page based on action type
  };

  const handleInviteStakeholder = (formData) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log('Invite stakeholder:', formData);
        resolve();
      }, 1000);
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Breadcrumbs />
      <main className="pt-4 pb-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Stakeholder Management</h1>
            <p className="text-muted-foreground">
              Coordinate with all transaction participants and manage role-based permissions
            </p>
          </div>

          {/* Stats Overview */}
          <StakeholderStats stats={stats} />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
            {/* Role Distribution */}
            <div className="xl:col-span-1">
              <RoleDistribution roleData={roleData} />
            </div>

            {/* Pending Actions */}
            <div className="xl:col-span-2">
              <PendingActions 
                pendingActions={pendingActions}
                onActionClick={handleActionClick}
              />
            </div>
          </div>

          {/* Filters */}
          <StakeholderFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            roleFilter={roleFilter}
            onRoleFilterChange={setRoleFilter}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            transactionFilter={transactionFilter}
            onTransactionFilterChange={setTransactionFilter}
            onClearFilters={handleClearFilters}
            onInviteStakeholder={() => setIsInviteModalOpen(true)}
          />

          {/* Stakeholder Table - Desktop */}
          <div className="hidden md:block">
            <StakeholderTable
              stakeholders={filteredStakeholders}
              onViewProfile={handleViewProfile}
              onEditPermissions={handleEditPermissions}
              onSendMessage={handleSendMessage}
              onAssignTransaction={handleAssignTransaction}
            />
          </div>

          {/* Stakeholder Cards - Mobile */}
          <div className="md:hidden">
            {filteredStakeholders?.length === 0 ? (
              <div className="bg-card rounded-lg border border-border shadow-soft p-8 text-center">
                <div className="text-muted-foreground mb-4">
                  <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">No stakeholders found</h3>
                <p className="text-muted-foreground">
                  {searchQuery || roleFilter !== 'all' || statusFilter !== 'all' ?'Try adjusting your filters to see more results.' :'Get started by inviting your first stakeholder.'
                  }
                </p>
              </div>
            ) : (
              filteredStakeholders?.map((stakeholder) => (
                <StakeholderMobileCard
                  key={stakeholder?.id}
                  stakeholder={stakeholder}
                  onViewProfile={handleViewProfile}
                  onEditPermissions={handleEditPermissions}
                  onSendMessage={handleSendMessage}
                  onAssignTransaction={handleAssignTransaction}
                />
              ))
            )}
          </div>

          {/* Results Summary */}
          {filteredStakeholders?.length > 0 && (
            <div className="mt-6 text-center text-sm text-muted-foreground">
              Showing {filteredStakeholders?.length} of {stakeholders?.length} stakeholders
            </div>
          )}
        </div>
      </main>
      {/* Invite Stakeholder Modal */}
      <InviteStakeholderModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvite={handleInviteStakeholder}
      />
    </div>
  );
};

export default StakeholderManagement;